import React, { useState, useEffect } from 'react';
import { cardAPI, authAPI } from '../../services/api';
import './CreditCardDetails.css';

const CARD_LOGOS = {
  'visa': '/card-logos/visa.svg',
  'mastercard': '/card-logos/mastercard.svg',
  'american express': '/card-logos/amex.svg',
  'amex': '/card-logos/amex.svg',
  'discover': '/card-logos/discover.svg',
};

const CardLogo = ({ cardType }) => {
  const type = (cardType || '').toLowerCase();
  const src = CARD_LOGOS[type];

  if (src) {
    return <img src={src} alt={cardType} className="card-logo-img" />;
  }

  return <span className="card-logo-text">{(cardType || 'CARD').toUpperCase()}</span>;
};

const CreditCardDetails = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCards = async () => {
      try {
        const user = authAPI.getCurrentUser();
        if (!user) return;

        const data = await cardAPI.getUserCards(user.id);
        setCards(data.cards || []);
        setError('');
      } catch (err) {
        setError('Failed to load credit cards');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCards();
  }, []);

  const getCardBrandColor = (cardType) => {
    const colors = {
      'visa': 'linear-gradient(135deg, #1434CB, #2E5CFF)',
      'mastercard': 'linear-gradient(135deg, #EB001B, #F79E1B)',
      'american express': 'linear-gradient(135deg, #006FCF, #00A3E0)',
      'amex': 'linear-gradient(135deg, #006FCF, #00A3E0)',
      'discover': 'linear-gradient(135deg, #FF6000, #FFA500)'
    };
    return colors[cardType?.toLowerCase()] || 'linear-gradient(135deg, #3b82f6, #8b5cf6)';
  };

  const formatCardNumber = (number) => {
    // VULNERABLE: Showing full card number instead of masking
    return number.match(/.{1,4}/g)?.join(' ') || number;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="credit-cards">
      <div className="cards-header">
        <h2>Credit Cards</h2>
        <div className="vulnerability-warning">
          <span className="warning-icon">⚠️</span>
          <span>VULNERABLE: Full card numbers and CVV exposed (PCI DSS violation)</span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {cards.length === 0 ? (
        <div className="no-cards">
          <p>No credit cards on file</p>
        </div>
      ) : (
        <div className="cards-grid">
          {cards.map((card) => (
            <div key={card.id} className="credit-card" style={{ background: getCardBrandColor(card.card_type) }}>
              <div className="card-front">
                <div className="card-chip">
                  <svg width="50" height="40" viewBox="0 0 50 40">
                    <rect width="50" height="40" rx="5" fill="#FFD700" opacity="0.8" />
                    <line x1="10" y1="15" x2="40" y2="15" stroke="#333" strokeWidth="1" />
                    <line x1="10" y1="25" x2="40" y2="25" stroke="#333" strokeWidth="1" />
                  </svg>
                </div>

                <div className="card-brand"><CardLogo cardType={card.card_type} /></div>

                <div className="card-number-container">
                  <div className="card-label">Card Number</div>
                  {/* VULNERABLE: Full card number exposed */}
                  <div className="card-number vulnerable-data">
                    {formatCardNumber(card.card_number)}
                  </div>
                </div>

                <div className="card-details">
                  <div className="card-holder">
                    <div className="card-label">Card Holder</div>
                    <div className="card-value">{card.cardholder_name}</div>
                  </div>

                  <div className="card-expiry">
                    <div className="card-label">Expires</div>
                    <div className="card-value">
                      {String(card.expiry_month).padStart(2, '0')}/{String(card.expiry_year).slice(-2)}
                    </div>
                  </div>

                  <div className="card-cvv">
                    <div className="card-label">CVV</div>
                    {/* VULNERABLE: CVV exposed in clear text */}
                    <div className="card-value vulnerable-data cvv-exposed">
                      {card.cvv} ⚠️
                    </div>
                  </div>
                </div>

                <div className="card-limits">
                  <div className="limit-item">
                    <span className="limit-label">Credit Limit:</span>
                    <span className="limit-value">${parseFloat(card.credit_limit || 0).toLocaleString()}</span>
                  </div>
                  <div className="limit-item">
                    <span className="limit-label">Available:</span>
                    <span className="limit-value available">${parseFloat(card.available_credit || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="pci-warning-box">
        <h3>⚠️ PCI DSS Compliance Violations</h3>
        <p>This application intentionally violates PCI Data Security Standards:</p>
        <ul>
          <li>✗ <strong>Full card numbers displayed</strong> (should be masked: ****1234)</li>
          <li>✗ <strong>CVV stored and displayed</strong> (should NEVER be stored)</li>
          <li>✗ <strong>Card data transmitted without encryption</strong></li>
          <li>✗ <strong>No tokenization</strong> (should use payment gateway tokens)</li>
        </ul>
        <p className="warning-note">
          In a real application, card data should be tokenized through a PCI-compliant payment processor.
        </p>
      </div>
    </div>
  );
};

export default CreditCardDetails;
